import { useMutation, UseMutationResult, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import { AffiliateDetail, CreateAffiliateDto } from '../domain/types';
import { affiliateManagementService } from '../infra/affiliateManagementService';

interface CreateAffiliateParams {
  projectId: string;
  body: CreateAffiliateDto;
}

interface UseCreateAffiliateResult {
  createAffiliate: (params: CreateAffiliateParams) => void;
  createAffiliateAsync: (params: CreateAffiliateParams) => Promise<AffiliateDetail>;
  isLoading: boolean;
  isError: boolean;
  isSuccess: boolean;
  error: Error | null;
}

export const useCreateAffiliate = (): UseCreateAffiliateResult => {
  const queryClient = useQueryClient();

  const {
    mutate,
    mutateAsync,
    isPending,
    isError,
    isSuccess,
    error,
  }: UseMutationResult<AffiliateDetail, Error, CreateAffiliateParams> = useMutation({
    mutationFn: async ({ projectId, body }: CreateAffiliateParams) => {
      return affiliateManagementService.create(projectId, body);
    },
    onSuccess: () => {
      toast.success('Affiliate created successfully');
      queryClient.invalidateQueries({
        queryKey: ['affiliate-management', 'list'],
      });
      queryClient.invalidateQueries({
        queryKey: ['project-affiliates'],
      });
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to create affiliate');
    },
  });

  return {
    createAffiliate: mutate,
    createAffiliateAsync: mutateAsync,
    isLoading: isPending,
    isError,
    isSuccess,
    error,
  };
};
